import { create } from "zustand";
import type { Track } from "../types";
import { localLibrary } from "../lib/library";
import { useFMRadioStore } from "./fmRadioStore";
import { recordDailyListen, getDailyQuota, triggerSubscriptionModal, resetSessionCountedTrack } from "../lib/quota";
import { api } from "../lib/api";

function recordTrackPlay(track: Track) {
  localLibrary.recordRecent(track);
  if (track?.id) {
    api.recordSongPlay(track.id).catch(() => {});
  }
}

export type PlayerState = {
  current: Track | null;
  queue: Track[];
  playing: boolean;
  repeatMode: "off" | "one" | "all";
  isShuffling: boolean;
  currentTime: number;
  duration: number;
  isBuffering: boolean;
  volume: number;
  seekTarget: number | null;
  play: (track: Track, queue?: Track[]) => void;
  toggle: () => void;
  pause: () => void;
  resume: () => void;
  next: () => void;
  previous: () => void;
  seek: (timeInSeconds: number) => void;
  clearSeekTarget: () => void;
  toggleShuffle: () => void;
  setRepeatMode: (mode: "off" | "one" | "all") => void;
  cycleRepeat: () => void;
  setVolume: (volume: number) => void;
  removeFromQueue: (songId: number | string) => void;
  clearQueue: () => void;
  setTracks: (tracks: Track[]) => void;
  close: () => void;
};

export const usePlayer = create<PlayerState>((set, get) => ({
  current: null,
  queue: [],
  playing: false,
  repeatMode: "all",
  isShuffling: false,
  currentTime: 0,
  duration: 0,
  isBuffering: false,
  volume: 1,
  seekTarget: null,

  play: (current, queue = [current]) => {
    try {
      useFMRadioStore.getState().stopCurrent();
    } catch {}

    const quota = recordDailyListen(current);
    if (!quota.allowed) {
      set({ playing: false });
      return;
    }

    recordTrackPlay(current);
    set({
      current,
      queue,
      playing: true,
      currentTime: 0,
      seekTarget: 0,
    });
  },

  toggle: () => {
    const nextPlaying = !get().playing;
    if (nextPlaying) {
      const currentTrack = get().current;
      if (!currentTrack?.ignore_limit) {
        const quota = getDailyQuota();
        if (!quota.isPremium && quota.remaining <= 0) {
          triggerSubscriptionModal();
          return;
        }
      }
      try {
        useFMRadioStore.getState().stopCurrent();
      } catch {}
    }
    set({ playing: nextPlaying });
  },
  pause: () => set({ playing: false }),
  resume: () => {
    const currentTrack = get().current;
    if (!currentTrack?.ignore_limit) {
      const quota = getDailyQuota();
      if (!quota.isPremium && quota.remaining <= 0) {
        triggerSubscriptionModal();
        return;
      }
    }
    try {
      useFMRadioStore.getState().stopCurrent();
    } catch {}
    set({ playing: true });
  },

  next: () => {
    const state = get();
    if (!state.current || !state.queue.length) return;

    if (state.isShuffling && state.queue.length > 1) {
      const remainingTracks = state.queue.filter((t) => t.id !== state.current?.id);
      const randomIndex = Math.floor(Math.random() * remainingTracks.length);
      const nextTrack = remainingTracks[randomIndex] || state.queue[0];
      const quota = recordDailyListen(nextTrack);
      if (!quota.allowed) {
        set({ playing: false });
        return;
      }
      recordTrackPlay(nextTrack);
      set({ current: nextTrack, playing: true, currentTime: 0, seekTarget: 0 });
      return;
    }

    const currentIndex = state.queue.findIndex((track) => track.id === state.current?.id);
    const nextIndex = currentIndex + 1;
    if (nextIndex < state.queue.length) {
      const nextTrack = state.queue[nextIndex];
      const quota = recordDailyListen(nextTrack);
      if (!quota.allowed) {
        set({ playing: false });
        return;
      }
      recordTrackPlay(nextTrack);
      set({ current: nextTrack, playing: true, currentTime: 0, seekTarget: 0 });
      return;
    }
    if (state.repeatMode === "all") {
      const nextTrack = state.queue[0];
      const quota = recordDailyListen(nextTrack);
      if (!quota.allowed) {
        set({ playing: false });
        return;
      }
      recordTrackPlay(nextTrack);
      set({ current: nextTrack, playing: true, currentTime: 0, seekTarget: 0 });
      return;
    }
    set({ playing: false });
  },

  previous: () => {
    const state = get();
    if (!state.current || !state.queue.length) return;

    // If more than 3 seconds in, restart the song
    if (state.currentTime > 3) {
      set({ currentTime: 0, seekTarget: 0 });
      return;
    }

    const currentIndex = state.queue.findIndex((track) => track.id === state.current?.id);
    if (currentIndex > 0) {
      const prevTrack = state.queue[currentIndex - 1];
      const quota = recordDailyListen(prevTrack);
      if (!quota.allowed) {
        set({ playing: false });
        return;
      }
      recordTrackPlay(prevTrack);
      set({ current: prevTrack, playing: true, currentTime: 0, seekTarget: 0 });
      return;
    }
    if (state.repeatMode === "all") {
      const prevTrack = state.queue[state.queue.length - 1];
      const quota = recordDailyListen(prevTrack);
      if (!quota.allowed) {
        set({ playing: false });
        return;
      }
      recordTrackPlay(prevTrack);
      set({ current: prevTrack, playing: true, currentTime: 0, seekTarget: 0 });
      return;
    }
    const prevTrack = state.queue[0];
    const quota = recordDailyListen(prevTrack);
    if (!quota.allowed) {
      set({ playing: false });
      return;
    }
    recordTrackPlay(prevTrack);
    set({ current: prevTrack, playing: true, currentTime: 0, seekTarget: 0 });
  },

  seek: (timeInSeconds: number) => {
    set({ currentTime: timeInSeconds, seekTarget: timeInSeconds });
  },

  clearSeekTarget: () => set({ seekTarget: null }),

  toggleShuffle: () => set((state) => ({ isShuffling: !state.isShuffling })),

  setRepeatMode: (mode) => set({ repeatMode: mode }),

  cycleRepeat: () =>
    set((state) => ({
      repeatMode:
        state.repeatMode === "all"
          ? "one"
          : state.repeatMode === "one"
          ? "off"
          : "all",
    })),

  setVolume: (volume) => set({ volume: Math.max(0, Math.min(1, volume)) }),

  removeFromQueue: (songId) =>
    set((state) => {
      const nextQueue = state.queue.filter((t) => String(t.id) !== String(songId));
      return { queue: nextQueue };
    }),

  clearQueue: () => set({ queue: [] }),

  setTracks: (tracks) => set({ queue: tracks }),

  close: () => {
    resetSessionCountedTrack();
    set({
      current: null,
      queue: [],
      playing: false,
      currentTime: 0,
      duration: 0,
      seekTarget: null,
    });
  },
}));
